import { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Eye, EyeOff, Trash2, Plus, MessageSquareWarning, FileText, Circle, CircleDashed, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import AgencyThreadsPanel from "./project/AgencyThreadsPanel";
import { ProjectBtsGallery } from "./project/ProjectBtsGallery";

// Staff-side manager for a project's agency Workspace surface: Open Items (E5/E6) + Document
// Room (E7). Populates what the agency portal reads via GET /agency/projects/{pid}/open-items
// and /documents. `partner_visible` (items) / visibility="partner" (docs) is what the agency sees;
// "Needs your input" flags an item so the agency can reply.
const OWNER_PARTIES = ["Partner", "NJM", "Crew"];
// Open Items are discrete action items → workflow states (progress), not RAG health.
const STATUSES = [
  { v: "Yet-to-begin", label: "Yet to begin", Icon: Circle },
  { v: "In-progress", label: "In progress", Icon: CircleDashed },
  { v: "Complete", label: "Complete", Icon: CheckCircle2 },
];
const STATUS_ICON = (status) => (STATUSES.find((s) => s.v.toLowerCase() === (status || "").toLowerCase())?.Icon) || Circle;
const DOC_TYPES = ["SOW", "Invoice", "Other"];
const DOC_STATUSES = ["Draft", "Sent", "Approved"];
// Audience decides who a document is for; a project has all three kinds and they must never mix.
// The agency portal shows ONLY "Partner Agency" docs.
const AUDIENCES = ["Client", "Production Partner", "Partner Agency"];
const docAudience = (d) => d.audience || (d.visibility === "partner" ? "Partner Agency" : "Client");
// Wave 3 (2026-09): a document with neither a file_id (Drive, proxied) nor an external_url
// (review link) is a legacy pasted `drive_url` row — fail-closed hidden from every agency
// response. Staff sees it flagged here and can replace it via the picker, or delete it.
const needsRelink = (d) => !(d.file_id || d.external_url);

export default function AgencyWorkspaceManager({ projectId, agencyRemoteId, includeBts = true }) {
  const { hasScope } = useAuth();
  const [items, setItems] = useState([]);
  const [docs, setDocs] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState("");
  const [loadErr, setLoadErr] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [docFormTarget, setDocFormTarget] = useState(null); // null | "new" | doc object being replaced
  const [showReqForm, setShowReqForm] = useState(false);
  // Agency Portal Stabilisation Part B (2026-10), item 3 — reassign picker's own options list.
  // Reuses the SAME `GET /v1/projects/pm-eligible-users` the project's own PM-assignment field
  // already calls (identical scope gate: founder/partner/pm), rather than building a duplicate.
  const [pmEligible, setPmEligible] = useState([]);

  const reload = useCallback(async () => {
    try {
      const [i, d, c] = await Promise.all([
        api.get(`/v1/build/projects/${projectId}/open-items`),
        api.get(`/v1/build/projects/${projectId}/documents`),
        api.get(`/v1/build/projects/${projectId}/crew-requirements`),
      ]);
      setItems(i.data.open_items || []);
      setDocs(d.data.documents || []);
      setReqs(c.data.requirements || []);
      setLoadErr(false);
    } catch { setLoadErr(true); }
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { api.get("/v1/projects/pm-eligible-users").then((r) => setPmEligible(r.data.items || [])).catch(() => {}); }, []);

  const upsertItem = async (payload) => {
    setBusy("item");
    try {
      await api.post(`/v1/build/projects/${projectId}/open-items`, { partner_agency_remote_id: agencyRemoteId, ...payload });
      await reload();
      return true;
    } catch { toast.error("Couldn't save open item"); return false; }
    finally { setBusy(""); }
  };

  const toggleItem = (it, field) => upsertItem({ id: it.id, [field]: !it[field] });
  const changeItemStatus = (it, status) => upsertItem({ id: it.id, status });
  const changeItemAssignee = (it, assignee_user_id) => upsertItem({ id: it.id, assignee_user_id: assignee_user_id || null });

  const deleteItem = async (id) => {
    setBusy(`del-item-${id}`);
    try { await api.delete(`/v1/build/projects/${projectId}/open-items/${id}`); await reload(); }
    catch { toast.error("Couldn't delete"); } finally { setBusy(""); }
  };

  const upsertDoc = async (payload) => {
    setBusy("doc");
    try {
      await api.post(`/v1/build/projects/${projectId}/documents`, { partner_agency_remote_id: agencyRemoteId, ...payload });
      await reload();
      return true;
    } catch { toast.error("Couldn't save document"); return false; }
    finally { setBusy(""); }
  };

  const changeDocAudience = (d, audience) => upsertDoc({ id: d.id, audience });

  const bulkSetDocTypeAudience = async (docType, audience) => {
    setBusy(`bulk-doc-${docType}`);
    try {
      await api.post(`/v1/build/projects/${projectId}/documents/bulk-audience`, { doc_type: docType, audience });
      await reload();
      toast.success(audience === "Partner Agency" ? `Shared all ${docType} documents with the agency` : `Stopped sharing all ${docType} documents with the agency`);
    } catch { toast.error("Couldn't update this folder's visibility"); } finally { setBusy(""); }
  };

  const deleteDoc = async (id) => {
    setBusy(`del-doc-${id}`);
    try { await api.delete(`/v1/build/projects/${projectId}/documents/${id}`); await reload(); }
    catch { toast.error("Couldn't delete"); } finally { setBusy(""); }
  };

  const previewDoc = async (d) => {
    try {
      const r = await api.get(`/v1/build/projects/${projectId}/documents/${d.id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Couldn't open this document"); }
  };

  const createReq = async (payload) => {
    setBusy("req");
    try {
      await api.post(`/v1/build/projects/${projectId}/crew-requirements`, { partner_agency_remote_id: agencyRemoteId, ...payload });
      await reload();
      return true;
    } catch { toast.error("Couldn't create requirement"); return false; }
    finally { setBusy(""); }
  };

  const deleteReq = async (id) => {
    setBusy(`del-req-${id}`);
    try { await api.delete(`/v1/build/projects/${projectId}/crew-requirements/${id}`); await reload(); }
    catch { toast.error("Couldn't delete"); } finally { setBusy(""); }
  };

  // Agency Portal Stabilisation, Wave A (2026-10) — doc_type is the closest thing this flat
  // document manifest has to a "folder"; group rows by it so one bulk action per group can
  // re-point every document of that type to the agency (or back) at once.
  const docsByType = DOC_TYPES.map((t) => ({ type: t, rows: docs.filter((d) => (d.doc_type || "Other") === t) })).filter((g) => g.rows.length > 0);

  return (
    <div className="mt-3 rounded-md border border-hairline bg-black/[0.015] p-3.5 space-y-5" data-testid={`agency-workspace-manager-${projectId}`}>
      {loadErr && (
        <div className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-700" data-testid={`agency-workspace-error-${projectId}`}>
          <span>Couldn't load this workspace. This is a load error, not an empty workspace.</span>
          <button onClick={reload} className="font-medium underline">Retry</button>
        </div>
      )}
      {/* Open Items */}
      <div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-medium uppercase tracking-wider text-faint">Open items · what the agency sees</div>
          <button onClick={() => setShowItemForm((s) => !s)} data-testid={`agency-add-open-item-toggle-${projectId}`}
            className="inline-flex items-center gap-1 text-[11px] text-brand-dim hover:underline"><Plus className="h-3 w-3" /> Add open item</button>
        </div>
        {items.length === 0 && !showItemForm && <div className="mt-1.5 text-[11px] text-faint">None yet. The agency's Open Items tab is empty until you add a partner-visible item.</div>}
        <div className="mt-2 space-y-1.5">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-2 rounded border border-hairline bg-paper px-2.5 py-1.5" data-testid={`agency-open-item-row-${it.id}`}>
              <div className="min-w-0">
                <div className="truncate text-xs text-ink">{it.title || "Untitled item"}</div>
                <div className="flex items-center gap-1 text-[10px] text-faint">
                  {(() => { const I = STATUS_ICON(it.status); return <I className="h-3 w-3" />; })()}
                  {it.created_by_party === "partner" ? "Agency request" : it.owner_party}{it.due ? ` · due ${it.due}` : ""}{it.needs_partner_input ? " · awaiting agency reply" : ""}
                  {it.created_by_party === "partner" ? ` · assigned to ${it.assignee_name || "no one yet"}` : ""}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {it.created_by_party === "partner" && (
                  <select value={it.assignee_user_id || ""} onChange={(e) => changeItemAssignee(it, e.target.value)} disabled={busy === "item"}
                    data-testid={`agency-item-assignee-select-${it.id}`}
                    title="Reassign: the agency created this and cannot pick who it goes to — only staff can."
                    className="rounded-full border border-hairline bg-black/5 px-2 py-0.5 text-[10px] text-faint">
                    <option value="">Unassigned</option>
                    {pmEligible.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                )}
                <select value={it.status} onChange={(e) => changeItemStatus(it, e.target.value)} disabled={busy === "item"}
                  data-testid={`agency-item-status-select-${it.id}`}
                  title="Status: the workflow state the agency sees on this item"
                  className="rounded-full border border-hairline bg-black/5 px-2 py-0.5 text-[10px] text-faint">
                  {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
                <button onClick={() => toggleItem(it, "partner_visible")} disabled={busy === "item"}
                  data-testid={`agency-item-visible-toggle-${it.id}`}
                  title={it.partner_visible ? "Visible to agency: click to hide" : "Hidden: click to show to agency"}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${it.partner_visible ? "bg-green-100 text-green-800" : "bg-black/5 text-faint"}`}>
                  {it.partner_visible ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}{it.partner_visible ? "Visible" : "Hidden"}
                </button>
                <button onClick={() => toggleItem(it, "needs_partner_input")} disabled={busy === "item"}
                  data-testid={`agency-item-needsinput-toggle-${it.id}`}
                  title={it.needs_partner_input ? "Needs agency input: click to clear" : "Flag: needs agency input"}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] transition-colors ${it.needs_partner_input ? "bg-amber-100 text-amber-800" : "border border-dashed border-black/15 text-faint hover:border-amber-400 hover:text-amber-700"}`}>
                  <MessageSquareWarning className="h-3 w-3" />{it.needs_partner_input ? "Needs input" : "Flag input"}
                </button>
                <button onClick={() => deleteItem(it.id)} disabled={busy === `del-item-${it.id}`}
                  data-testid={`agency-item-delete-${it.id}`} className="text-faint hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
        {showItemForm && <OpenItemForm projectId={projectId} busy={busy === "item"} onSave={async (p) => { if (await upsertItem(p)) setShowItemForm(false); }} onCancel={() => setShowItemForm(false)} />}
      </div>

      {/* Documents */}
      <div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-medium uppercase tracking-wider text-faint">Documents · publish to the agency</div>
          <button onClick={() => setDocFormTarget((t) => (t ? null : "new"))} data-testid={`agency-add-doc-toggle-${projectId}`}
            className="inline-flex items-center gap-1 text-[11px] text-brand-dim hover:underline"><Plus className="h-3 w-3" /> Publish a document</button>
        </div>
        {docs.length === 0 && !docFormTarget && <div className="mt-1.5 text-[11px] text-faint">None yet. Publish a Drive file or a review link to fill the agency's Documents tab.</div>}
        <div className="mt-2 space-y-3">
          {docsByType.map(({ type, rows }) => {
            const allAgency = rows.every((d) => docAudience(d) === "Partner Agency");
            return (
              <div key={type} data-testid={`agency-doc-type-group-${type}`}>
                <div className="flex items-center justify-between rounded-t border border-hairline border-b-0 bg-black/[0.03] px-2.5 py-1">
                  <span className="text-[10px] font-medium uppercase tracking-wider text-faint">{type} · {rows.length} doc{rows.length === 1 ? "" : "s"}</span>
                  <button onClick={() => bulkSetDocTypeAudience(type, allAgency ? "Client" : "Partner Agency")}
                    disabled={busy === `bulk-doc-${type}`} data-testid={`agency-doc-bulk-audience-toggle-${type}`}
                    title="Folder-level toggle: set every document of this type at once"
                    className={`rounded-full border px-2 py-0.5 text-[10px] ${allAgency ? "border-green-200 bg-green-100 text-green-800" : "border-hairline bg-paper text-faint hover:bg-black/5"}`}>
                    {busy === `bulk-doc-${type}` ? "Working…" : allAgency ? "Stop sharing all with agency" : "Share all with agency"}
                  </button>
                </div>
                <div className="space-y-1.5 rounded-b border border-hairline border-t-0 p-1.5">
                  {rows.map((d) => {
                    const relink = needsRelink(d);
                    return (
                      <div key={d.id} className="flex items-center justify-between gap-2 rounded border border-hairline bg-paper px-2.5 py-1.5" data-testid={`agency-doc-row-${d.id}`}>
                        <div className="min-w-0">
                          <div className="truncate text-xs text-ink"><FileText className="mr-1 inline h-3 w-3 text-faint" />{d.name || "Untitled"} <span className="text-[10px] text-faint">· {d.doc_type} v{d.version} · {d.status}</span></div>
                          {relink ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800" data-testid={`agency-doc-needs-relink-${d.id}`}>
                              <MessageSquareWarning className="h-3 w-3" /> Needs re-link — pasted link, not proxied
                            </span>
                          ) : d.source === "external_link" ? (
                            <a href={d.external_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-brand-dim hover:underline" data-testid={`agency-doc-external-link-${d.id}`}>{d.external_url}</a>
                          ) : (
                            <button onClick={() => previewDoc(d)} data-testid={`agency-doc-preview-${d.id}`} className="text-[10px] text-brand-dim hover:underline">Preview (streamed, same as agency sees)</button>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {relink && <button onClick={() => setDocFormTarget(d)} disabled={!!docFormTarget}
                            data-testid={`agency-doc-relink-btn-${d.id}`} className="rounded-full border border-amber-300 px-2 py-0.5 text-[10px] text-amber-800 hover:bg-amber-50">Replace</button>}
                          <select value={docAudience(d)} onChange={(e) => changeDocAudience(d, e.target.value)} disabled={busy === "doc"}
                            data-testid={`agency-doc-audience-select-${d.id}`}
                            title="Audience: who this document is for. Only 'Partner Agency' docs appear in the agency portal."
                            className={`rounded-full border px-2 py-0.5 text-[10px] ${docAudience(d) === "Partner Agency" ? "border-green-200 bg-green-100 text-green-800" : "border-hairline bg-black/5 text-faint"}`}>
                            {AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
                          </select>
                          <button onClick={() => deleteDoc(d.id)} disabled={busy === `del-doc-${d.id}`}
                            data-testid={`agency-doc-delete-${d.id}`} className="text-faint hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        {docFormTarget && (
          <DocForm projectId={projectId} doc={docFormTarget === "new" ? null : docFormTarget} busy={busy === "doc"}
            onSave={async (p) => { if (await upsertDoc(p)) setDocFormTarget(null); }} onCancel={() => setDocFormTarget(null)} />
        )}
      </div>

      {/* Crew requirements: a PM seeds one so the agency's Preferred-crew tab isn't a dead end */}
      <div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-medium uppercase tracking-wider text-faint">Crew requirements · unblock the agency's Preferred-crew tab</div>
          <button onClick={() => setShowReqForm((s) => !s)} data-testid={`agency-add-req-toggle-${projectId}`}
            className="inline-flex items-center gap-1 text-[11px] text-brand-dim hover:underline"><Plus className="h-3 w-3" /> Add requirement</button>
        </div>
        {reqs.length === 0 && !showReqForm && <div className="mt-1.5 text-[11px] text-faint">None yet. Add a role (e.g. Director of Photography) so the agency can shortlist crew against it.</div>}
        <div className="mt-2 space-y-1.5">
          {reqs.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 rounded border border-hairline bg-paper px-2.5 py-1.5" data-testid={`agency-req-row-${r.id}`}>
              <div className="min-w-0">
                <div className="truncate text-xs text-ink">{r.role} <span className="text-[10px] text-faint">× {r.qty || 1}{r.location ? ` · ${r.location}` : ""}</span></div>
                {r.capability && <div className="truncate text-[10px] text-faint">{r.capability}</div>}
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${r.status === "Sent" ? "bg-green-100 text-green-800" : "bg-black/5 text-faint"}`}>{r.status || "Draft"}</span>
                <button onClick={() => deleteReq(r.id)} disabled={busy === `del-req-${r.id}`}
                  data-testid={`agency-req-delete-${r.id}`} className="text-faint hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
        {showReqForm && <ReqForm busy={busy === "req"} onSave={async (p) => { if (await createReq(p)) setShowReqForm(false); }} onCancel={() => setShowReqForm(false)} />}
      </div>
      {/* Regression v6 fix pass (2026-10), Part 3 — BTS visibility now lives in THIS shared
          component too, so Partners > {agency} > manage workspace can share/un-share stills
          without going hunting for the project page. Same `ProjectBtsGallery` the Project page
          renders (its new `mode="agency"`: no upload, no client toggle, no delete) — never a
          second copy. `includeBts={false}` on the Project page only because the FULL gallery is
          already on that same Communication tab, a few rows above this panel. */}
      {includeBts && hasScope("client_portal.admin") && (
        <div data-testid="agency-workspace-bts-section">
          <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-faint">Behind the scenes · what the agency sees</div>
          <ProjectBtsGallery projectId={projectId} mode="agency" />
        </div>
      )}
      <AgencyThreadsPanel projectId={projectId} />
    </div>
  );
}

const fieldCls = "w-full rounded-md border border-hairline bg-paper px-2.5 py-1.5 text-xs text-ink";

function OpenItemForm({ projectId, busy, onSave, onCancel }) {
  const [title, setTitle] = useState("");
  const [owner, setOwner] = useState("Partner");
  const [status, setStatus] = useState("Yet-to-begin");
  const [due, setDue] = useState("");
  const [visible, setVisible] = useState(true);
  const [needsInput, setNeedsInput] = useState(true);
  return (
    <div className="mt-3 space-y-2 rounded-md border border-hairline bg-paper p-3" data-testid={`agency-open-item-form-${projectId}`}>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Item title (e.g. Approve updated SOW)"
        data-testid="agency-item-title-input" className={fieldCls} />
      <div className="grid grid-cols-3 gap-2">
        <select value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="agency-item-owner-select" className={fieldCls}>
          {OWNER_PARTIES.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="agency-item-status-select" className={fieldCls}>
          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="agency-item-due-input" className={fieldCls} />
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="inline-flex items-center gap-1.5 text-[11px] text-ink"><input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} data-testid="agency-item-visible-check" /> Visible to agency</label>
        <label className="inline-flex items-center gap-1.5 text-[11px] text-ink"><input type="checkbox" checked={needsInput} onChange={(e) => setNeedsInput(e.target.checked)} data-testid="agency-item-needsinput-check" /> Needs the agency's input</label>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button disabled={busy || !title.trim()} data-testid="agency-item-save-btn"
          onClick={() => onSave({ title: title.trim(), owner_party: owner, status, due: due || undefined, partner_visible: visible, needs_partner_input: needsInput })}
          className="rounded-md bg-ink px-3 py-1.5 text-xs text-white disabled:opacity-50">{busy ? "Saving…" : "Save item"}</button>
        <button onClick={onCancel} className="rounded-md border border-hairline px-3 py-1.5 text-xs text-faint">Cancel</button>
      </div>
    </div>
  );
}

function DocForm({ projectId, doc, busy, onSave, onCancel }) {
  const [docType, setDocType] = useState(doc?.doc_type || "SOW");
  const [name, setName] = useState(doc?.name || "");
  const [mode, setMode] = useState(doc?.source === "external_link" ? "link" : "drive");
  const [fileId, setFileId] = useState("");
  const [fileName, setFileName] = useState("");
  const [url, setUrl] = useState(doc?.external_url || "");
  const [version, setVersion] = useState(String(doc?.version || 1));
  const [status, setStatus] = useState(doc?.status || "Sent");
  const [audience, setAudience] = useState(doc ? docAudience(doc) : "Partner Agency");
  const [files, setFiles] = useState(null);
  const [filesErr, setFilesErr] = useState("");

  const loadFiles = useCallback(async () => {
    setFiles("loading");
    try {
      const r = await api.get(`/v1/build/projects/${projectId}/documents/drive-files`);
      setFiles(r.data.items || []);
      setFilesErr("");
    } catch (e) {
      setFiles([]);
      setFilesErr(e?.response?.data?.detail?.code === "project_missing_drive_folder"
        ? "This project has no Drive folder yet — upload something to it first."
        : "Couldn't load this project's Drive files.");
    }
  }, [projectId]);

  useEffect(() => { if (mode === "drive" && files === null) loadFiles(); }, [mode, files, loadFiles]);

  const canSave = name.trim() && (mode === "drive" ? !!fileId : !!url.trim());

  return (
    <div className="mt-3 space-y-2 rounded-md border border-hairline bg-paper p-3" data-testid="agency-doc-form">
      {doc && <div className="text-[11px] font-medium text-amber-800">Replacing a legacy pasted link — choose a Drive file or paste a review link below.</div>}
      <div className="grid grid-cols-2 gap-2">
        <select value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="agency-doc-type-select" className={fieldCls}>
          {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Document name" data-testid="agency-doc-name-input" className={fieldCls} />
      </div>
      <div className="flex gap-1 rounded-md bg-black/5 p-0.5 text-[11px]">
        <button type="button" onClick={() => setMode("drive")} data-testid="agency-doc-mode-drive-btn"
          className={`flex-1 rounded px-2 py-1 ${mode === "drive" ? "bg-white shadow-sm text-ink" : "text-faint"}`}>Choose from Drive</button>
        <button type="button" onClick={() => setMode("link")} data-testid="agency-doc-mode-link-btn"
          className={`flex-1 rounded px-2 py-1 ${mode === "link" ? "bg-white shadow-sm text-ink" : "text-faint"}`}>External review link</button>
      </div>
      {mode === "drive" ? (
        <div className="max-h-56 overflow-y-auto rounded border border-hairline" data-testid="agency-doc-drive-picker">
          {files === "loading" && <div className="p-2 text-[11px] text-faint">Loading this project's Drive files…</div>}
          {filesErr && <div className="p-2 text-[11px] text-red-600" data-testid="agency-doc-drive-picker-error">{filesErr}</div>}
          {Array.isArray(files) && files.length === 0 && !filesErr && <div className="p-2 text-[11px] text-faint">No files found (folders named "_Internal (NJM only)" are always excluded).</div>}
          {Array.isArray(files) && files.map((f) => (
            <button key={f.id} type="button" disabled={f.too_large || f.already_registered}
              onClick={() => { setFileId(f.id); setFileName(f.name); if (!name.trim()) setName(f.name); }}
              data-testid={`agency-doc-drive-file-${f.id}`}
              title={f.too_large ? "Too large for the portal (500MB cap) — use a review link instead" : f.already_registered ? "Already published" : ""}
              className={`flex w-full items-center justify-between gap-2 border-b border-hairline px-2 py-1.5 text-left text-[11px] last:border-0 ${fileId === f.id ? "bg-brand-dim/10" : "hover:bg-black/[0.02]"} ${(f.too_large || f.already_registered) ? "cursor-not-allowed opacity-40" : ""}`}>
              <span className="truncate">{f.path}{f.name}</span>
              {f.too_large ? <span className="shrink-0 text-amber-700">Too large</span> : f.already_registered ? <span className="shrink-0 text-faint">Published</span> : null}
            </button>
          ))}
          {fileId && <div className="border-t border-hairline bg-green-50 px-2 py-1 text-[11px] text-green-800" data-testid="agency-doc-drive-selected">Selected: {fileName || fileId}</div>}
        </div>
      ) : (
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Review link (Frame.io, Vimeo, YouTube…)" data-testid="agency-doc-url-input" className={fieldCls} />
      )}
      <div className="grid grid-cols-2 gap-2">
        <input type="number" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="Version" data-testid="agency-doc-version-input" className={fieldCls} />
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="agency-doc-status-select" className={fieldCls}>
          {DOC_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div>
        <label className="mb-1 block text-[10px] uppercase tracking-wider text-faint">Audience: who is this document for?</label>
        <select value={audience} onChange={(e) => setAudience(e.target.value)} data-testid="agency-doc-audience-select" className={fieldCls}>
          {AUDIENCES.map((a) => <option key={a} value={a}>{a}{a === "Partner Agency" ? " (agency sees this)" : ""}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button disabled={busy || !canSave} data-testid="agency-doc-save-btn"
          onClick={() => onSave({ id: doc?.id, doc_type: docType, title: name.trim(), version: version === "" ? 1 : Number(version), status, audience,
            ...(mode === "drive" ? { file_id: fileId } : { external_url: url.trim() }) })}
          className="rounded-md bg-ink px-3 py-1.5 text-xs text-white disabled:opacity-50">{busy ? "Publishing…" : doc ? "Save" : "Publish document"}</button>
        <button onClick={onCancel} className="rounded-md border border-hairline px-3 py-1.5 text-xs text-faint">Cancel</button>
      </div>
    </div>
  );
}


function ReqForm({ busy, onSave, onCancel }) {
  const [role, setRole] = useState("");
  const [qty, setQty] = useState("1");
  const [location, setLocation] = useState("");
  const [capability, setCapability] = useState("");
  return (
    <div className="mt-3 space-y-2 rounded-md border border-hairline bg-paper p-3" data-testid="agency-req-form">
      <div className="grid grid-cols-3 gap-2">
        <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Role (e.g. Director of Photography)"
          data-testid="agency-req-role-input" className={`col-span-2 ${fieldCls}`} />
        <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Qty"
          data-testid="agency-req-qty-input" className={fieldCls} />
      </div>
      <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location (optional)" data-testid="agency-req-location-input" className={fieldCls} />
      <input value={capability} onChange={(e) => setCapability(e.target.value)} placeholder="Capability / notes (optional)" data-testid="agency-req-capability-input" className={fieldCls} />
      <div className="flex items-center gap-2 pt-1">
        <button disabled={busy || !role.trim()} data-testid="agency-req-save-btn"
          onClick={() => onSave({ role: role.trim(), qty: qty === "" ? 1 : Number(qty), location: location.trim() || undefined, capability: capability.trim() || undefined })}
          className="rounded-md bg-ink px-3 py-1.5 text-xs text-white disabled:opacity-50">{busy ? "Creating…" : "Create requirement"}</button>
        <button onClick={onCancel} className="rounded-md border border-hairline px-3 py-1.5 text-xs text-faint">Cancel</button>
      </div>
    </div>
  );
}
